
import { PageContainer } from "@/components/ui/page-container";
import { AppointmentList } from "@/components/appointments/AppointmentList";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useState } from "react";
import { AppointmentForm } from "@/components/appointments/AppointmentForm";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppointmentStats } from "@/components/appointments/AppointmentStats";
import { AppointmentInsights } from "@/components/appointments/AppointmentInsights";

const Appointments = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  const { data: appointments, isLoading, refetch } = useQuery({
    queryKey: ["appointments"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("appointments")
        .select("*")
        .eq("user_id", user.id);
        
      if (error) throw error;
      
      // Transform appointment data to match expected format
      return data?.map(apt => ({
        id: apt.id,
        doctor_name: apt.doctor_name,
        location: apt.location,
        date: apt.date,
        notes: apt.notes,
        service: { service_name: apt.specialty || "Consultation" },
        status: "confirmed"
      })) || [];
    },
  });

  // Filtrer les rendez-vous par date
  const upcomingAppointments = appointments?.filter(apt => new Date(apt.date) >= new Date()) || [];
  const pastAppointments = appointments?.filter(apt => new Date(apt.date) < new Date()) || [];
  
  // Calculer les rendez-vous du mois en cours
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const thisMonthAppointments = appointments?.filter(apt => {
    const aptDate = new Date(apt.date);
    return aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear;
  }) || [];

  return (
    <PageContainer 
      title="Rendez-vous"
      variant="professional"
      className="py-6"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            Planifiez et gérez vos rendez-vous médicaux en toute simplicité.
          </p>
          <Button 
            onClick={() => setIsFormOpen(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="mr-2 h-4 w-4" /> Ajouter un rendez-vous
          </Button>
        </div>

        <AppointmentForm 
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          onSuccess={() => {
            refetch();
          }}
        />
        
        <div className="grid gap-6 md:grid-cols-2">
          <AppointmentStats 
            appointments={appointments || []}
            upcomingAppointments={upcomingAppointments}
            pastAppointments={pastAppointments}
            thisMonthAppointments={thisMonthAppointments}
          />
          <AppointmentInsights />
        </div>
        
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-48 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <AppointmentList 
              title="Rendez-vous à venir" 
              appointments={upcomingAppointments} 
            />
            <AppointmentList 
              title="Rendez-vous passés" 
              appointments={pastAppointments} 
              isPast={true}
            />
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default Appointments;
